import { FramingRow } from "@/types/contentFraming";
import { logger } from "../tslog";
import { useSharePricePerformanceStore } from "@/state/sharePricePerformanceStore";
import { sendBulletPointsToExtension, sendSpeakerNotesToExtension } from "../powerpointExtensionService";

// Normalize incoming color strings to hex (#RRGGBB). Supports #RGB/#RRGGBB and rgb()/rgba()
function normalizeToHexColor(color: any): string | undefined {
  if (typeof color !== "string" || !color) return undefined;
  const trimmed = color.trim();
  // #RRGGBB
  if (/^#([0-9a-fA-F]{6})$/.test(trimmed)) return trimmed.toUpperCase();
  // #RGB → #RRGGBB
  if (/^#([0-9a-fA-F]{3})$/.test(trimmed)) {
    const r = trimmed[1];
    const g = trimmed[2];
    const b = trimmed[3];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  // rgb()/rgba()
  const rgbMatch = trimmed.match(/^rgba?\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(\d*\.?\d+))?\s*\)$/i);
  if (rgbMatch) {
    const r = Math.max(0, Math.min(255, parseInt(rgbMatch[1], 10)));
    const g = Math.max(0, Math.min(255, parseInt(rgbMatch[2], 10)));
    const b = Math.max(0, Math.min(255, parseInt(rgbMatch[3], 10)));
    const toHex = (n: number) => n.toString(16).padStart(2, '0').toUpperCase();
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  return undefined;
}


export async function insertTextIntoShape(
  context: PowerPoint.RequestContext,
  shapeId: string,
  slideId: string,
  text: string,
): Promise<void> {
  try {
    if (!text || text.trim() === '') {
      logger.warn("[ContentFraming] No text provided; skipping insertion", {
        shapeId,
        slideId,
      });
      return;
    }

    // Get the slide
    const slide = context.presentation.slides.getItem(slideId);
    slide.load(["id"]);
    await context.sync();

    // Get the shape
    const shape = slide.shapes.getItem(shapeId);
    shape.load(["id", "textFrame"]);
    await context.sync();

    // Check if shape has textFrame
    if (!shape.textFrame) {
      logger.warn(`[ContentFraming] Shape ${shapeId} has no textFrame; skipping`, {
        shapeId,
        slideId,
      });
      return;
    }

    // Get textRange
    const textRange = shape.textFrame.textRange;
    if (!textRange) {
      logger.warn(`[ContentFraming] Shape ${shapeId} has no textRange; skipping`, {
        shapeId,
        slideId,
      });
      return;
    }

    // Load text property
    textRange.load(["text"]);
    await context.sync();

    // Insert the text
    textRange.text = text;
    await context.sync();

    try {
      shape.load(["top", "height"]);
      await context.sync();
    } catch {}
    // Bold only the prefix for Source/Notes, keep the rest normal
    try {
      const full = String(textRange.text || "");
      const lower = full.toLowerCase();
      let header: string | null = null;
      if (lower.startsWith("source:")) {
        header = "Source:";
      } else if (lower.startsWith("notes:")) {
        header = "Notes:";
      }

      if (header) {
        const headerLen = Math.min(header.length, full.length);
        const headerRange = textRange.getSubstring(0, headerLen);
        if (headerRange?.font) headerRange.font.bold = true;

        const restLen = Math.max(full.length - headerLen, 0);
        if (restLen > 0) {
          const restRange = textRange.getSubstring(headerLen, restLen);
          if (restRange?.font) restRange.font.bold = false;
        }
        await context.sync();
      }

      // If it's a Notes shape, adjust height and top based on number of lines
      if (lower.startsWith("notes:")) {
        // Count lines AFTER the "Notes:" header
        const body = full.slice("Notes:".length);
        const lines = body.split(/(?:\r\n|\r|\n)/).filter((l) => String(l).trim().length > 0).length || 1;

        // Load current geometry
        

        // 12pt per line; move top up by newHeight
        const newHeight = Math.max((lines * 12), 20);
        const newTopAdjustment = newHeight - shape.height;
        try {
          shape.height = newHeight;
          shape.top = Math.max(0, shape.top - newTopAdjustment);
          await context.sync();
        } catch {}
      }
    } catch {}

    logger.info(`[ContentFraming] Successfully inserted text into shape`, {
      shapeId,
      slideId,
      textLength: text.length,
    });

  } catch (error) {
    logger.error(`[ContentFraming] Failed to insert text into shape`, {
      error: (error as any)?.message || String(error),
      shapeId,
      slideId,
    });
    throw error;
  }
}

export async function insertTextIntoShapeWithFormatting(
  context: PowerPoint.RequestContext,
  shapeId: string,
  slideId: string,
  text: string,
  rowId?: string,
): Promise<void> {
  // First insert text (and handle header bolding/notes geometry) using the base function
  await insertTextIntoShape(context, shapeId, slideId, text);

  try {
    const state: any = useSharePricePerformanceStore.getState();
    const fonts = state?.metaData?.fontsSection || {};

    const key = {
      slideTitle: fonts?.slideTitle,
      slideSubTitle: fonts?.slideSubTitle,
      keyTakeAways: fonts?.keyTakeAways,
      placeholderTitle: fonts?.placeholderTitle,
      placeholderSubTitle: fonts?.placeholderSubTitle,
      source: fonts?.source,
      notes: fonts?.footnotes,
    }

    const style = key[rowId as keyof typeof key];

    // Load slide/shape and apply only allowed font properties
    const slide = context.presentation.slides.getItem(slideId);
    slide.load(["shapes"]);
    await context.sync();
    const shape = slide.shapes.getItem(shapeId);
    shape.load(["textFrame"]);
    await context.sync();

    const tr = shape?.textFrame?.textRange as any;
    if (!tr) return;
    tr.load(["text"]);
    await context.sync();

    const full = String(tr.text || "");
    const fullRange = tr.getSubstring(0, full.length);
    if (fullRange?.font) {
      const sizeVal = Number(style?.size);
      const colorVal = String(style?.color || '').trim();
      const boldVal = !!style?.bold;
      const italicVal = !!style?.italic;
      const underlineVal = !!style?.underline;

      if (!Number.isNaN(sizeVal) && sizeVal > 0) fullRange.font.size = sizeVal;
      if (colorVal) {
        const normalized = normalizeToHexColor(colorVal) || colorVal;
        fullRange.font.color = normalized;
      }
      fullRange.font.bold = boldVal;
      fullRange.font.italic = italicVal;
      fullRange.font.underline = underlineVal && "Single";
    }
    await context.sync();

  } catch (e) {
    try {
      logger.warn('[ContentFraming] Failed to apply formatting from metadata', {
        error: (e as any)?.message || String(e),
        shapeId,
        slideId,
      });
    } catch {}
  }
}

export const insertDataToPlaceholder = async (): Promise<void> => {
  try {
    // Get the current state from the local storage
    const rows = JSON.parse(localStorage.getItem("contentFramingRowsData") || "[]");
    
    // Filter rows that have complete shape data
    const completeRows: FramingRow[] = rows.filter((row: FramingRow) => {
      return (
        row.enabled
      );
    });

    // Log the complete row data
    logger.info("[ContentFraming] Inserting data to placeholders", {
      completeRowsCount: completeRows.length,
    });

    if (completeRows.length === 0) {
      logger.info("[ContentFraming] No complete rows found to insert");
      return;
    }

    // Insert text into each shape
    await PowerPoint.run(async (context) => {
      for (const row of completeRows) {
        try {
            const placeholder = row.placeholder!;
            console.log("Placeholder", placeholder);
            let inputText = row.inputText;
            
            if(row.id === 'script'){  
              // Send speaker notes to PowerPoint Extension
              await sendSpeakerNotesToExtension(placeholder.slideId, inputText);
              continue;
            }

          // Call the inserter function with the data
          if(row.mode === 'formatTemplate'){
            await insertTextIntoShapeWithFormatting(
              context,
              placeholder.shapeId,
              placeholder.slideId,
              inputText,
              row.id,
            );
          } else {
            await insertTextIntoShape(
              context,
              placeholder.shapeId,
              placeholder.slideId,
              inputText,
            );
          }
          if (row.id === 'keyTakeAways'){
            await sendBulletPointsToExtension(placeholder.slideId, placeholder.shapeId, inputText, {
              height: placeholder.height,
              width: placeholder.width,
              top: placeholder.top,
              left: placeholder.left,
            }); 
            continue;
          }

        } catch (error) {
          logger.error(`[ContentFraming] Failed to insert text for row ${row.id}`, {
            error: (error as any)?.message || String(error),
            rowId: row.id,
            shapeId: row.placeholder?.shapeId,
            slideId: row.placeholder?.slideId,
          });
        }
      }
    });

  } catch (error) {
    logger.error("[ContentFraming] Error in insertDataToPlaceholder", {
      error: (error as any)?.message || String(error),
    });
  }
};
